import { useMemo, useState } from 'react';
import { AutocompleteInput } from '@/components';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useGetBranchesByCommerceQuery } from '@/pages/Business/pages/PickupCycles/services';
import { PickupBranch } from '@/pages/Business/pages/PickupCycles/models';

interface BranchFilterAutocompleteProps {
  commerceId: string;
  value: string;
  onChange: (branchId: string) => void;
}

const isBranch = (data: unknown): data is PickupBranch =>
  typeof data === 'object' && data !== null && 'id' in data;

// Máximo de opciones a renderizar por búsqueda; las sucursales del comercio se
// cargan y se filtran en el front de forma incremental.
const MAX_OPTIONS = 50;

const BranchFilterAutocomplete = ({
  commerceId,
  value,
  onChange,
}: BranchFilterAutocompleteProps) => {
  const [input, setInput] = useState('');
  const { data: branches = [], isFetching } = useGetBranchesByCommerceQuery(
    { commerceId },
    { skip: !commerceId },
  );

  const options: Option[] = useMemo(() => {
    const query = input.trim().toLowerCase();
    const filtered = query
      ? branches.filter((branch) => branch.name.toLowerCase().includes(query))
      : branches;
    return filtered
      .slice(0, MAX_OPTIONS)
      .map((branch) => ({ label: branch.name, data: branch }));
  }, [branches, input]);

  const selectedOption: Option | null = value
    ? {
        label:
          branches.find((branch) => branch.id === value)?.name ??
          'Sucursal seleccionada',
        data: value,
      }
    : null;

  const handleChange = (option: Option | null) => {
    if (option && isBranch(option.data)) {
      onChange(option.data.id);
      return;
    }
    onChange('');
  };

  const placeholder = !commerceId
    ? 'Sucursal (elegí un comercio primero)'
    : 'Sucursal';

  return (
    <AutocompleteInput
      name='branchFilter'
      placeholder={placeholder}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={setInput}
      options={options}
      loading={isFetching}
      disabled={!commerceId}
      showTypingLengthHint={false}
    />
  );
};

export default BranchFilterAutocomplete;
